import { Store } from 'redux';
import { createFetchRetry } from '@rsos/capstone';
import { getAPIHost } from '@rsos/utils/metaTags';

export interface DiscrepancySettings {
  targetState: string;
  clientID: string;
  clientSecret: string;
}

export const DEFAULT_SETTINGS: DiscrepancySettings = {
  targetState: '',
  clientID: '',
  clientSecret: '',
};

/**
 * Parses raw PSAP attributes into discrepancy settings
 */
function parseDiscrepancySettings(rawData: unknown): DiscrepancySettings {
  if (typeof rawData !== 'object' || rawData === null) {
    return DEFAULT_SETTINGS;
  }

  const data = rawData as Record<string, unknown>;

  const settings = {
    targetState:
      typeof data['indigital_discrepancies_target_state'] === 'string'
        ? data['indigital_discrepancies_target_state']
        : '',
    clientID:
      typeof data['indigital_discrepancies_client_id'] === 'string'
        ? data['indigital_discrepancies_client_id']
        : '',
    clientSecret:
      typeof data['indigital_discrepancies_client_secret'] === 'string'
        ? data['indigital_discrepancies_client_secret']
        : '',
  };

  return settings;
}

/**
 * Fetches discrepancy settings for a given organization.
 *
 * @param orgName - The organization name to fetch settings for
 * @param store - Redux store for createFetchRetry
 * @returns Promise resolving to discrepancy settings
 */
export async function fetchDiscrepancySettings(
  orgName: string,
  store: Store,
): Promise<DiscrepancySettings> {
  if (!orgName) {
    throw new Error(
      'Organization name is required to fetch discrepancy settings',
    );
  }

  const fetchRetry = createFetchRetry(store, { shouldThrowResponse: false });
  const apiHost = getAPIHost();
  const url = `${apiHost}/v1/capstone/psaps/${orgName}/attributes`;

  const response = await fetchRetry(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch discrepancy settings: ${response.status} ${response.statusText}`,
    );
  }

  const data: unknown = await response.json();
  return parseDiscrepancySettings(data);
}

/**
 * Updates discrepancy settings for a given organization.
 *
 * @param orgName - The organization name to update settings for
 * @param settings - The settings to save
 * @param store - Redux store for createFetchRetry
 */
export async function updateDiscrepancySettings(
  orgName: string,
  settings: DiscrepancySettings,
  store: Store,
): Promise<void> {
  if (!orgName) {
    throw new Error(
      'Organization name is required to update discrepancy settings',
    );
  }

  const fetchRetry = createFetchRetry(store);
  const apiHost = getAPIHost();
  const url = `${apiHost}/v1/capstone/psaps/${orgName}`;

  const payload = {
    attributes: {
      indigital_discrepancies_target_state: settings.targetState,
      indigital_discrepancies_client_id: settings.clientID,
      indigital_discrepancies_client_secret: settings.clientSecret,
    },
  };

  const response = await fetchRetry(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(
      `Failed to update discrepancy settings: ${response.status} ${response.statusText}`,
    );
  }
}
